import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/ui/status-badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, History } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

export default function MateriaisPage() {
  const { user } = useAuth();
  const podeVerPreco = ['admin', 'financeiro'].includes(user?.perfil);
  const [precos, setPrecos] = useState({});
  const [preco, setPreco] = useState('');
  const [materiais, setMateriais] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [filteredMateriais, setFilteredMateriais] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    categoria: 'instrumental',
    material_fabricacao: '',
    metodo_esterilizacao: ['vapor'],
    vida_util_dias: 365,
    validade_pos_esterilizacao_dias: 30,
    cliente_id: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    fetchMateriais();
    fetchClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!podeVerPreco) return;
    api.get('/financeiro/precos')
      .then(r => setPrecos(Object.fromEntries(r.data.filter(p => p.valor_unitario != null).map(p => [p.material_id, p.valor_unitario]))))
      .catch(() => {});
  }, [podeVerPreco]);

  const salvarPreco = async (materialId) => {
    if (!podeVerPreco || preco === '') return;
    const valor = parseFloat(String(preco).replace(',', '.'));
    if (isNaN(valor) || valor < 0) { toast.error('Preço inválido'); return; }
    if (precos[materialId] === valor) return;
    await api.post('/financeiro/precos', { material_id: materialId, valor_unitario: valor });
    setPrecos(p => ({ ...p, [materialId]: valor }));
  };

  const fetchMateriais = async () => {
    try {
      const response = await api.get('/materiais');
      setMateriais(response.data);
      setFilteredMateriais(response.data);
    } catch (error) {
      toast.error('Erro ao carregar materiais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const filtered = materiais.filter(material =>
      material.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (material.codigo && material.codigo.toLowerCase().includes(searchTerm.toLowerCase())) ||
      material.categoria.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredMateriais(filtered);
  }, [searchTerm, materiais]);

  const fetchClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMaterial) {
        await api.put(`/materiais/${editingMaterial.id}`, formData);
        await salvarPreco(editingMaterial.id);
        toast.success('Material atualizado com sucesso!');
      } else {
        const res = await api.post('/materiais', formData);
        if (res.data?.id) await salvarPreco(res.data.id);
        toast.success('Material criado com sucesso!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchMateriais();
    } catch (error) {
      toast.error('Erro ao salvar material');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este material?')) return;
    try {
      await api.delete(`/materiais/${id}`);
      toast.success('Material excluído com sucesso!');
      fetchMateriais();
    } catch (error) {
      toast.error('Erro ao excluir material');
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      nome: material.nome,
      categoria: material.categoria,
      material_fabricacao: material.material_fabricacao,
      metodo_esterilizacao: material.metodo_esterilizacao,
      vida_util_dias: material.vida_util_dias,
      validade_pos_esterilizacao_dias: material.validade_pos_esterilizacao_dias,
      cliente_id: material.cliente_id || '',
    });
    setPreco(precos[material.id] != null ? String(precos[material.id]).replace('.', ',') : '');
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingMaterial(null);
    setPreco('');
    setFormData({
      nome: '',
      categoria: 'instrumental',
      material_fabricacao: '',
      metodo_esterilizacao: ['vapor'],
      vida_util_dias: 365,
      validade_pos_esterilizacao_dias: 30,
      cliente_id: '',
    });
  };

  const handleMetodoChange = (metodo) => {
    const current = [...formData.metodo_esterilizacao];
    if (current.includes(metodo)) {
      setFormData({ ...formData, metodo_esterilizacao: current.filter(m => m !== metodo) });
    } else {
      setFormData({ ...formData, metodo_esterilizacao: [...current, metodo] });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div data-testid="materiais-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Materiais
          </h1>
          <p className="text-lg text-slate-600 mt-2">Gerenciar produtos e materiais</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="btn-add-material"
              onClick={() => resetForm()}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Material
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingMaterial ? 'Editar Material' : 'Novo Material'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="nome">Nome do Material</Label>
                  <Input
                    data-testid="input-nome"
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                    required
                  />
                  <p className="text-xs text-slate-500 mt-1">Código será gerado automaticamente</p>
                </div>
                <div>
                  <Label htmlFor="categoria">Categoria</Label>
                  <select
                    data-testid="select-categoria"
                    id="categoria"
                    value={formData.categoria}
                    onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="instrumental">Instrumental</option>
                    <option value="caixa_cirurgica">Caixa Cirúrgica</option>
                    <option value="kit">Kit</option>
                    <option value="material_medico">Material Médico</option>
                    <option value="estetica">Estética</option>
                    <option value="odontologico">Odontológico</option>
                    <option value="manicure">Manicure</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="cliente_id">Cliente (Opcional)</Label>
                  <select
                    data-testid="select-cliente"
                    id="cliente_id"
                    value={formData.cliente_id}
                    onChange={(e) => setFormData({ ...formData, cliente_id: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="">Nenhum</option>
                    {clientes.map(c => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label htmlFor="material_fabricacao">Material de Fabricação</Label>
                  <Input
                    data-testid="input-material-fabricacao"
                    id="material_fabricacao"
                    value={formData.material_fabricacao}
                    onChange={(e) => setFormData({ ...formData, material_fabricacao: e.target.value })}
                    required
                  />
                </div>
                <div className="col-span-2">
                  <Label>Métodos de Esterilização Permitidos</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {['vapor', 'oxido_etileno', 'peroxido_hidrogenio', 'formaldeido'].map((metodo) => (
                      <label key={metodo} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.metodo_esterilizacao.includes(metodo)}
                          onChange={() => handleMetodoChange(metodo)}
                          className="rounded border-slate-300"
                        />
                        <span className="text-sm text-slate-700">{metodo.replace('_', ' ')}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="vida_util_dias">Vida Útil (dias)</Label>
                  <Input
                    data-testid="input-vida-util"
                    id="vida_util_dias"
                    type="number" onFocus={(e) => e.target.select()}
                    value={formData.vida_util_dias}
                    onChange={(e) => setFormData({ ...formData, vida_util_dias: parseInt(e.target.value) })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="validade_pos_esterilizacao_dias">Validade Pós-Esterilização (dias)</Label>
                  <Input
                    data-testid="input-validade"
                    id="validade_pos_esterilizacao_dias"
                    type="number" onFocus={(e) => e.target.select()}
                    value={formData.validade_pos_esterilizacao_dias}
                    onChange={(e) => setFormData({ ...formData, validade_pos_esterilizacao_dias: parseInt(e.target.value) })}
                    required
                  />
                </div>
                {podeVerPreco && (
                  <div className="col-span-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                    <Label htmlFor="preco">Preço para orçamento (R$)</Label>
                    <Input
                      data-testid="input-preco-material"
                      id="preco"
                      inputMode="decimal"
                      placeholder="Ex: 3,00"
                      value={preco}
                      onChange={(e) => setPreco(e.target.value.replace(/[^\d,.]/g, ''))}
                      className="mt-1 bg-white"
                    />
                    <p className="text-xs text-emerald-800 mt-1">Visível só para Administrador e Financeiro. Usado automaticamente nos orçamentos.</p>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="btn-submit" type="submit" className="bg-blue-600 hover:bg-blue-700">
                  {editingMaterial ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="max-w-md">
          <Label htmlFor="search">Buscar materiais</Label>
          <Input
            id="search"
            placeholder="Buscar por nome, código ou categoria..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mt-1"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Código</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Nome</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Categoria</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Métodos</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredMateriais.map((material) => (
              <tr key={material.id} data-testid={`material-row-${material.id}`} className="hover:bg-slate-50">
                <td className="px-6 py-4 text-sm font-medium text-slate-900">{material.codigo}</td>
                <td className="px-6 py-4 text-sm text-slate-900">{material.nome}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{material.categoria}</td>
                <td className="px-6 py-4 text-xs text-slate-600">
                  {material.metodo_esterilizacao.join(', ')}
                </td>
                <td className="px-6 py-4">
                  <StatusBadge status={material.status} />
                </td>
                <td className="px-6 py-4 text-sm">
                  <div className="flex gap-2">
                    <Button
                      data-testid={`btn-edit-${material.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(material)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      data-testid={`btn-delete-${material.id}`}
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(material.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredMateriais.length === 0 && searchTerm && (
          <div className="text-center py-12 text-slate-500">
            Nenhum material encontrado para "{searchTerm}"
          </div>
        )}
        {materiais.length === 0 && !searchTerm && (
          <div className="text-center py-12 text-slate-500">
            Nenhum material cadastrado
          </div>
        )}
      </div>
    </div>
  );
}